import { provideHttpClient, withXhr } from '@angular/common/http';
import {
  ApplicationConfig,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  importProvidersFrom,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { OktaAuthModule, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import type { AuthState } from '@okta/okta-auth-js';
import { TranslateModule } from '@ngx-translate/core';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { AuthModule } from "@auth0/auth0-angular";
// Import from library path to ensure same token instance
import { ApiPartnerConfig, ApiPartnerEnum, provideRdvClient } from '@claas-rdv/rdv-client';
import { resolveLanguage } from './services/language.service';
import { registerSupportedLocales } from './locale-registry';

/**
 * The UI language for this page load, resolved once at bootstrap from
 * `?lang=` → browser preferences → `environment.defaultLanguage`.
 *
 * Resolved eagerly because `LOCALE_ID` is fixed for the lifetime of the
 * application and must be provided before Angular bootstraps. After an OAuth
 * redirect the `lang` parameter is no longer in the URL; `InboundComponent`
 * restores it and switches the UI at runtime via `LanguageService`.
 */
const resolvedLanguage = resolveLanguage();

// Locale data must be registered before LOCALE_ID is used by any pipe.
registerSupportedLocales();

// index.html ships a static lang="en"; correct it to what we actually render.
document.documentElement.lang = resolvedLanguage;

function getApiPartnerConfig(): ApiPartnerConfig {
  const urlParams = new URLSearchParams(window.location.search);
  const apiPartner = urlParams.get('apiPartner')?.toUpperCase();

  switch (apiPartner) {
    case ApiPartnerEnum.CC3:
      return {
        partnerSystem: ApiPartnerEnum.CC3,
        apiBaseUrl: environment.apiBaseUrls.CC3,
        language: resolvedLanguage
      };
    case ApiPartnerEnum.SOFF:
      return {
        partnerSystem: ApiPartnerEnum.SOFF,
        apiBaseUrl: environment.apiBaseUrls.SOFF,
        language: resolvedLanguage
      };
    default:
      return {
        partnerSystem: ApiPartnerEnum.SOFF,
        apiBaseUrl: environment.apiBaseUrls.SOFF,
        language: resolvedLanguage
      };
  }
}

export const oauthConfig = {
  clientId: environment.okta.clientId,
  issuer: environment.okta.issuer,
  redirectUri: environment.okta.redirectUri,
  scopes: ['openid', 'profile', 'email'],
  pkce: true,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSessionExpired: () => {},
  testing: {
    disableHttpsCheck: true,
  },
  // interclient_token handling is done in InboundComponent, not here.
  // transformAuthState only returns the current auth state as-is.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transformAuthState: async (_oktaAuth: any, authState: AuthState) => {
    return authState;
  },
  // Override restoreOriginalUri to prevent OktaAuthModule's default implementation
  // from navigating via Angular Router after handleLoginRedirect().
  // We handle the Okta callback entirely in InboundComponent using token.parseFromUrl()
  // and manage navigation ourselves. Without this no-op, the default restoreOriginalUri
  // (set by OktaAuthFactoryService.setupOktaAuth) would navigate back to the same route
  // causing a loop, since the redirectUri IS the InboundComponent route.
  // Note: setupOktaAuth only sets its default when `!oktaAuth.options.restoreOriginalUri`,
  // so by providing one here, the factory will skip its own.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  restoreOriginalUri: async () => {},
};

export const oktaAuth = new OktaAuth(oauthConfig);

// Auth0 SDK by default intercepts any URL with ?code=&state= and tries to
// exchange the code for a token. When Okta redirects back to
// /security/okta/login?code=...&state=..., Auth0 sees these params,
// tries to process them, fails (no Auth0 transaction), and navigates to "/".
// skipRedirectCallback tells Auth0 to ignore ?code=&state= on non-Auth0 routes.
//const isOktaCallbackRoute = window.location.pathname.startsWith('/security/okta/login');
const isOktaCallbackRoute = window.location.pathname.startsWith('/security/okta/login');
export const appConfig: ApplicationConfig = {
  providers: [
    // TranslateModule must be initialized BEFORE provideRdvClient()
    // so that the library can merge its translations into the existing TranslateService
    importProvidersFrom(TranslateModule.forRoot()),
    provideRdvClient(getApiPartnerConfig()),
    // Drives DatePipe & friends; fixed for the application lifetime, so it uses
    // the language resolved at bootstrap even if the UI switches later.
    { provide: LOCALE_ID, useValue: resolvedLanguage },
    provideHttpClient(withXhr()),
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes),
    provideZonelessChangeDetection(),
    importProvidersFrom([
        OktaAuthModule.forRoot({ oktaAuth }),
        AuthModule.forRoot({
          ...environment.auth0,
          skipRedirectCallback: isOktaCallbackRoute,
        })
    ]),
    OktaAuthStateService,
  ],
};
